import { type TrackByFunction } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  type IBrand,
  type IBrandQueryRequest,
  type IPractice,
  type IPracticeQueryRequest,
  QueryScopeConfigId,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  type INamedDocument,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IQueryScopeForm<T, QueryScopeRequest extends object> {
  scopeId: QueryScopeConfigId;
  formGroup: TypedFormGroup<QueryScopeRequest>;
  options$: Observable<T[]>;
  trackBy: TrackByFunction<T>;
  isShown$: Observable<boolean>;
  getDefaultValue(
    overrides?: Partial<QueryScopeRequest>
  ): Promise<QueryScopeRequest>;
}

export class BrandQueryScopeForm
  implements IQueryScopeForm<INamedDocument<IBrand>, IBrandQueryRequest>
{
  scopeId = QueryScopeConfigId.Brand;
  trackBy = TrackByFunctions.ref<INamedDocument<IBrand>>();
  isShown$: Observable<boolean>;
  formGroup = new TypedFormGroup<IBrandQueryRequest>({
    brandRef: new TypedFormControl<DocumentReference<IBrand>>(
      undefined,
      Validators.required
    ),
  });

  constructor(
    public options$: Observable<INamedDocument<IBrand>[]>,
    private _currentBrand$: Observable<WithRef<IBrand> | undefined>
  ) {
    this.isShown$ = options$.pipe(map((options) => options.length > 1));
  }

  async getDefaultValue(
    overrides?: Partial<IBrandQueryRequest>
  ): Promise<IBrandQueryRequest> {
    const brand$ = combineLatest([this._currentBrand$, this.options$]).pipe(
      map(([currentBrand, brands]) => currentBrand ?? first(brands)),
      filterUndefined()
    );
    const brand = await snapshot(brand$);
    return {
      brandRef: brand.ref,
      ...overrides,
    };
  }
}

export class PracticeQueryScopeForm
  implements IQueryScopeForm<INamedDocument<IPractice>, IPracticeQueryRequest>
{
  scopeId = QueryScopeConfigId.Practice;
  trackBy = TrackByFunctions.ref<INamedDocument<IPractice>>();
  isShown$: Observable<boolean>;
  formGroup = new TypedFormGroup<IPracticeQueryRequest>({
    practiceRefs: new TypedFormControl<DocumentReference<IPractice>[]>(
      undefined,
      Validators.required
    ),
  });

  constructor(public options$: Observable<INamedDocument<IPractice>[]>) {
    this.isShown$ = options$.pipe(map((options) => options.length > 1));
  }

  async getDefaultValue(
    overrides?: Partial<IPracticeQueryRequest>
  ): Promise<IPracticeQueryRequest> {
    const practices = await snapshot(this.options$);

    return {
      practiceRefs: practices.map((practice) => practice.ref),
      ...overrides,
    };
  }
}

export interface IQueryScopeForms {
  [QueryScopeConfigId.Brand]: BrandQueryScopeForm;
  [QueryScopeConfigId.Practice]: PracticeQueryScopeForm;
}

export class QueryScopeForms implements IQueryScopeForms {
  [QueryScopeConfigId.Brand]: BrandQueryScopeForm;
  [QueryScopeConfigId.Practice]: PracticeQueryScopeForm;
  isShown$: Observable<boolean>;

  constructor(
    brands$: Observable<WithRef<IBrand>[]>,
    practices$: Observable<WithRef<IPractice>[]>,
    currentBrand$: Observable<WithRef<IBrand> | undefined>
  ) {
    this[QueryScopeConfigId.Brand] = new BrandQueryScopeForm(
      brands$,
      currentBrand$
    );
    this[QueryScopeConfigId.Practice] = new PracticeQueryScopeForm(practices$);

    this.isShown$ = combineLatest([
      this[QueryScopeConfigId.Brand].isShown$,
      this[QueryScopeConfigId.Practice].isShown$,
    ]).pipe(map(([brandShown, practiceShown]) => brandShown || practiceShown));
  }
}
